import { useRef, useEffect } from 'react';
import type {
  ContextMenuState,
  Edge,
  EdgeRoutingMode,
  Layer,
  Node,
  NodeGroup,
  SequenceMessage,
  SequenceParticipant,
  SequenceParticipantGroup,
  ThemePalette,
} from '../../types';
import { NODE_TYPES } from '../../constants';
import { GENERAL_SHAPE_TYPES, isMarkupType } from '../../constants/nodeTypes';

interface ContextMenuProps {
  contextMenu: ContextMenuState;
  onClose: () => void;
  palette: Pick<ThemePalette, 'panelBg' | 'panelBorder' | 'panelText' | 'panelSubText'>;
  glass: React.CSSProperties;
  defaultRoutingMode: EdgeRoutingMode;

  // Data
  nodes: Node[];
  edges: Edge[];
  groups: NodeGroup[];
  layers: Layer[];
  participants: SequenceParticipant[];
  participantGroups: SequenceParticipantGroup[];
  messages: SequenceMessage[];
  selectedItems: string[];

  // Node handlers
  onRotateGeneralShape: (nodeId: string) => void;
  onAddSocket: (nodeId: string, type: 'input' | 'output') => void;
  onRotateNodeDirection: (nodeId: string) => void;
  onRenameNode: (nodeId: string) => void;
  onViewNodeInfo: (nodeId: string) => void;
  onChangeNodeType: (nodeId: string, newType: string) => void;
  onEnterGraph: (nodeId: string) => void;
  onSwitchPage: (pageId: string) => void;
  onUnlinkPage: (nodeId: string) => void;
  onLinkPage: (nodeId: string) => void;
  onCreateChildView: (nodeId: string) => void;
  onCreateGroup: (nodeIds: string[]) => void;
  onDissolveGroup: (nodeId: string) => void;
  onSetNodeLayer: (nodeId: string, layerId: string | undefined) => void;
  onRemoveNode: (nodeId: string) => void;

  // Edge handlers
  onEditEdgeLabel: (edgeId: string) => void;
  onAddEdgeAnchor: (edgeId: string, x: number, y: number) => void;
  onClearEdgeAnchors: (edgeId: string) => void;
  onToggleEdgeRoutingMode: (edgeId: string, current: EdgeRoutingMode) => void;
  onToggleEdgeAnimation: (edgeId: string, current: boolean) => void;
  onRemoveEdge: (edgeId: string) => void;

  // Group handlers
  onRenameGroup: (groupId: string) => void;
  onToggleGroupCollapse: (groupId: string) => void;
  onUngroupNodes: (groupId: string) => void;
  onOpenGroupColorPicker: (groupId: string) => void;

  // Participant handlers
  onViewParticipantInfo: (participantId: string) => void;
  onRenameParticipant: (participantId: string) => void;
  onGroupParticipants: (participantIds: string[]) => void;
  onSetParticipantLayer: (participantId: string, layerId: string | undefined) => void;
  onRemoveParticipant: (participantId: string) => void;

  // Participant-group handlers
  onToggleParticipantGroupCollapse: (groupId: string) => void;
  onPromptRenameParticipantGroup: (groupId: string) => void;
  onUngroupParticipants: (groupId: string) => void;

  // Message handlers
  onViewMessageInfo: (messageId: string) => void;
  onEditMessageLabel: (messageId: string) => void;
  onUpdateMessageType: (messageId: string, type: 'sync' | 'async' | 'return') => void;
  onUpdateMessageColor: (messageId: string, color: string | undefined) => void;
  onCopyMessage: (message: SequenceMessage) => void;
  onCutMessage: (message: SequenceMessage) => void;
  onRemoveMessage: (messageId: string) => void;

  // Undo
  snapshotBeforeMutation: () => void;
}

const MESSAGE_COLORS: ReadonlyArray<string> = [
  '#6366f1',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#f97316',
  '#ec4899',
  '#64748b',
];

const SWITCHABLE_NODE_TYPES = [
  'service',
  'database',
  'api',
  'person',
  'system',
  'external',
  'logic',
] as const;

const NODE_TYPES_BLOCKING_TYPE_SWITCH = ['diamond', 'queue', 'group', 'waypoint', 'group_input', 'group_output', 'box', 'oval', 'rounded', 'hexagon', 'triangle', 'cross', 'text', 'note', 'arrow', 'line'];
const NODE_TYPES_BLOCKING_DIRECTION_ROTATE = ['diamond', 'database', 'queue', 'waypoint', 'group_input', 'group_output', 'box', 'oval', 'rounded', 'hexagon', 'triangle', 'cross', 'text', 'note', 'arrow', 'line'];
const BOUNDARY_NODE_TYPES = ['group_input', 'group_output'];

/**
 * Right-click context menu for nodes, edges, groups, sequence participants,
 * participant groups, and sequence messages.
 *
 * The menu is purely presentational and delegates every action via the
 * `on*` props supplied by the host.  It self-closes when the user clicks
 * outside of its bounds.
 */
export function ContextMenu({
  contextMenu,
  onClose,
  palette,
  glass,
  defaultRoutingMode,
  nodes,
  edges,
  groups,
  layers,
  participants,
  participantGroups,
  messages,
  selectedItems,
  onRotateGeneralShape,
  onAddSocket,
  onRotateNodeDirection,
  onRenameNode,
  onViewNodeInfo,
  onChangeNodeType,
  onEnterGraph,
  onSwitchPage,
  onUnlinkPage,
  onLinkPage,
  onCreateChildView,
  onCreateGroup,
  onDissolveGroup,
  onSetNodeLayer,
  onRemoveNode,
  onEditEdgeLabel,
  onAddEdgeAnchor,
  onClearEdgeAnchors,
  onToggleEdgeRoutingMode,
  onToggleEdgeAnimation,
  onRemoveEdge,
  onRenameGroup,
  onToggleGroupCollapse,
  onUngroupNodes,
  onOpenGroupColorPicker,
  onViewParticipantInfo,
  onRenameParticipant,
  onGroupParticipants,
  onSetParticipantLayer,
  onRemoveParticipant,
  onToggleParticipantGroupCollapse,
  onPromptRenameParticipantGroup,
  onUngroupParticipants,
  onViewMessageInfo,
  onEditMessageLabel,
  onUpdateMessageType,
  onUpdateMessageColor,
  onCopyMessage,
  onCutMessage,
  onRemoveMessage,
  snapshotBeforeMutation,
}: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contextMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as HTMLElement)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu, onClose]);

  if (!contextMenu) return null;

  const selectedNodeIds = selectedItems.filter((id) => nodes.some((n) => n.id === id));

  return (
    <div
      ref={menuRef}
      className="fixed z-[200] min-w-[180px] rounded-xl shadow-2xl border overflow-hidden backdrop-blur-md"
      style={{ left: contextMenu.x, top: contextMenu.y, ...glass }}
    >
      {contextMenu.type === 'node' && (() => {
        const ctxNode = nodes.find((n) => n.id === contextMenu.nodeId);
        return (
          <div className="py-1">
            {ctxNode && GENERAL_SHAPE_TYPES.has(ctxNode.type) ? (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onRotateGeneralShape(contextMenu.nodeId);
                  onClose();
                }}
              >
                ↻ Rotate Shape
              </button>
            ) : ctxNode?.type === 'box' ? (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onRenameNode(contextMenu.nodeId);
                  onClose();
                }}
              >
                ✏️ Rename
              </button>
            ) : ctxNode && isMarkupType(ctxNode.type) ? (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onRenameNode(contextMenu.nodeId);
                  onClose();
                }}
              >
                {ctxNode.type === 'text' || ctxNode.type === 'note' ? '✏️ Edit text' : '✏️ Add label'}
              </button>
            ) : ctxNode?.type === 'group_input' ? (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onAddSocket(contextMenu.nodeId, 'output');
                  onClose();
                }}
              >
                Add Output Socket
              </button>
            ) : ctxNode?.type === 'group_output' ? (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onAddSocket(contextMenu.nodeId, 'input');
                  onClose();
                }}
              >
                Add Input Socket
              </button>
            ) : (
              <>
                <button
                  className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                  style={{ color: palette.panelText }}
                  onClick={() => {
                    onAddSocket(contextMenu.nodeId, 'input');
                    onClose();
                  }}
                >
                  Add Input Socket
                </button>
                <button
                  className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                  style={{ color: palette.panelText }}
                  onClick={() => {
                    onAddSocket(contextMenu.nodeId, 'output');
                    onClose();
                  }}
                >
                  Add Output Socket
                </button>
              </>
            )}

            {ctxNode && !NODE_TYPES_BLOCKING_DIRECTION_ROTATE.includes(ctxNode.type) && (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onRotateNodeDirection(contextMenu.nodeId);
                  onClose();
                }}
              >
                ↻ Rotate Direction ({((ctxNode.data?.direction as string) ?? 'ltr').toUpperCase()})
              </button>
            )}

            {ctxNode && !BOUNDARY_NODE_TYPES.includes(ctxNode.type) && !isMarkupType(ctxNode.type) && (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onRenameNode(contextMenu.nodeId);
                  onClose();
                }}
              >
                ✏️ Rename Node
              </button>
            )}

            <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />

            {ctxNode && !BOUNDARY_NODE_TYPES.includes(ctxNode.type) && (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onViewNodeInfo(contextMenu.nodeId);
                  onClose();
                }}
              >
                ℹ View Info {ctxNode?.meta?.status ? `· ${ctxNode.meta.status}` : ''}
              </button>
            )}

            {ctxNode && !NODE_TYPES_BLOCKING_TYPE_SWITCH.includes(ctxNode.type) && (
              <>
                <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
                <div
                  className="px-4 py-2 text-xs uppercase tracking-wider"
                  style={{ color: palette.panelSubText }}
                >
                  Change Type
                </div>
                {SWITCHABLE_NODE_TYPES.map((t) => {
                  const tm = NODE_TYPES[t as keyof typeof NODE_TYPES];
                  const isActive = ctxNode.type === t;
                  return (
                    <button
                      key={t}
                      onClick={() => {
                        onChangeNodeType(contextMenu.nodeId, t);
                        onClose();
                      }}
                      className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                      style={{
                        color: isActive ? tm.color : palette.panelText,
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      {isActive ? '✓ ' : '  '}
                      {tm.label}
                    </button>
                  );
                })}
              </>
            )}

            {ctxNode?.childGraphId && (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: '#818cf8' }}
                onClick={() => {
                  onEnterGraph(contextMenu.nodeId);
                  onClose();
                }}
              >
                ▶ Enter Group (Tab)
              </button>
            )}

            {ctxNode && !BOUNDARY_NODE_TYPES.includes(ctxNode.type) && !isMarkupType(ctxNode.type) &&
              (ctxNode.linkedPageId ? (
                <>
                  <button
                    className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                    style={{ color: '#10b981' }}
                    onClick={() => {
                      if (ctxNode.linkedPageId) onSwitchPage(ctxNode.linkedPageId);
                      onClose();
                    }}
                  >
                    ⇒ Go to Linked Page
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                    style={{ color: palette.panelSubText }}
                    onClick={() => {
                      onUnlinkPage(contextMenu.nodeId);
                      onClose();
                    }}
                  >
                    ✕ Remove Page Link
                  </button>
                </>
              ) : (
                <>
                <button
                  className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                  style={{ color: palette.panelSubText }}
                  onClick={() => {
                    onCreateChildView(contextMenu.nodeId);
                    onClose();
                  }}
                >
                  ⊕ Create container view
                </button>
                <button
                  className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                  style={{ color: palette.panelSubText }}
                  onClick={() => {
                    onLinkPage(contextMenu.nodeId);
                    onClose();
                  }}
                >
                  ⊞ Link to Page…
                </button>
                </>
              ))}

            <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />

            {selectedNodeIds.length >= 1 && (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  const ids = [...new Set([...selectedNodeIds, contextMenu.nodeId])].filter((id) => {
                    const n = nodes.find((nd) => nd.id === id);
                    return n && !n.childGraphId && !BOUNDARY_NODE_TYPES.includes(n.type) && !isMarkupType(n.type);
                  });
                  if (ids.length >= 2) {
                    snapshotBeforeMutation();
                    onCreateGroup(ids);
                  }
                  onClose();
                }}
              >
                ⊞ Group into architecture (Ctrl+G) — enter with Tab
              </button>
            )}

            {ctxNode?.childGraphId && (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  snapshotBeforeMutation();
                  onDissolveGroup(contextMenu.nodeId);
                  onClose();
                }}
              >
                ⊟ Dissolve Group
              </button>
            )}

            {layers.length > 1 && (
              <>
                <div className="px-4 py-1 text-xs uppercase tracking-wider" style={{ color: palette.panelSubText }}>
                  Assign to Layer
                </div>
                {layers.map((layer) => {
                  const isCurrent = (ctxNode?.layerId ?? 'default') === layer.id;
                  return (
                    <button
                      key={layer.id}
                      className="w-full text-left px-6 py-1.5 text-sm transition-colors flex items-center gap-2 hover:bg-white/5"
                      style={{ color: isCurrent ? '#818cf8' : palette.panelText }}
                      onClick={() => {
                        onSetNodeLayer(contextMenu.nodeId, layer.id === 'default' ? undefined : layer.id);
                        onClose();
                      }}
                    >
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: layer.color }} />
                      {layer.name}
                      {isCurrent ? ' ✓' : ''}
                    </button>
                  );
                })}
                <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
              </>
            )}

            <button
              className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
              onClick={() => {
                onRemoveNode(contextMenu.nodeId);
                onClose();
              }}
            >
              Delete Node
            </button>
          </div>
        );
      })()}

      {contextMenu.type === 'edge' && (() => {
        const ctxEdge = edges.find((e) => e.id === contextMenu.edgeId);
        const currentRouting = ctxEdge?.routingMode ?? defaultRoutingMode;
        return (
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
              style={{ color: palette.panelText }}
              onClick={() => {
                onEditEdgeLabel(contextMenu.edgeId);
                onClose();
              }}
            >
              ✏️ Edit Label
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
              style={{ color: palette.panelText }}
              onClick={() => {
                onAddEdgeAnchor(contextMenu.edgeId, contextMenu.x, contextMenu.y);
                onClose();
              }}
            >
              ⚓ Add Anchor Here
            </button>
            {(ctxEdge?.waypoints?.length ?? 0) > 0 && (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelSubText }}
                onClick={() => {
                  onClearEdgeAnchors(contextMenu.edgeId);
                  onClose();
                }}
              >
                ✕ Clear All Anchors
              </button>
            )}
            <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
            <button
              className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
              style={{ color: palette.panelText }}
              onClick={() => {
                onToggleEdgeRoutingMode(contextMenu.edgeId, currentRouting);
                onClose();
              }}
            >
              {currentRouting === 'orthogonal' ? '⤵ Switch to Bezier' : '⊞ Switch to Orthogonal'}
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
              style={{ color: palette.panelText }}
              onClick={() => {
                onToggleEdgeAnimation(contextMenu.edgeId, !!ctxEdge?.animated);
                onClose();
              }}
            >
              {ctxEdge?.animated ? 'Stop Animation' : 'Toggle Flow Animation'}
            </button>
            <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
            <button
              className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
              onClick={() => {
                onRemoveEdge(contextMenu.edgeId);
                onClose();
              }}
            >
              Delete Edge
            </button>
          </div>
        );
      })()}

      {contextMenu.type === 'group' && (() => {
        const ctxGroup = groups.find((g) => g.id === contextMenu.groupId);
        const isFlowGroup = !!ctxGroup;
        const isPGroup = participantGroups.some((g) => g.id === contextMenu.groupId);
        if (isFlowGroup && ctxGroup?.isFrame)
          return (
            <div className="py-1">
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onRenameGroup(contextMenu.groupId);
                  onClose();
                }}
              >
                Rename Frame
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onOpenGroupColorPicker(contextMenu.groupId);
                  onClose();
                }}
              >
                Change Color
              </button>
              <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
              <button
                className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                onClick={() => {
                  snapshotBeforeMutation();
                  onUngroupNodes(contextMenu.groupId);
                  onClose();
                }}
              >
                Delete Frame
              </button>
            </div>
          );
        if (isFlowGroup)
          return (
            <div className="py-1">
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onToggleGroupCollapse(contextMenu.groupId);
                  onClose();
                }}
              >
                {ctxGroup?.collapsed ? '▸ Expand Group' : '▾ Collapse Group'}
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onRenameGroup(contextMenu.groupId);
                  onClose();
                }}
              >
                Rename Group
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onUngroupNodes(contextMenu.groupId);
                  onClose();
                }}
              >
                Ungroup All
              </button>
              <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
              <button
                className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                onClick={() => {
                  snapshotBeforeMutation();
                  onUngroupNodes(contextMenu.groupId);
                  onClose();
                }}
              >
                Delete Group
              </button>
            </div>
          );
        if (isPGroup) {
          const pg = participantGroups.find((g) => g.id === contextMenu.groupId);
          return (
            <div className="py-1">
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  snapshotBeforeMutation();
                  onToggleParticipantGroupCollapse(contextMenu.groupId);
                  onClose();
                }}
              >
                {pg?.collapsed ? '▸ Expand Group' : '▾ Collapse Group'}
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  onPromptRenameParticipantGroup(contextMenu.groupId);
                  onClose();
                }}
              >
                Rename Group
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  snapshotBeforeMutation();
                  onUngroupParticipants(contextMenu.groupId);
                  onClose();
                }}
              >
                Ungroup All
              </button>
              <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
              <button
                className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                onClick={() => {
                  snapshotBeforeMutation();
                  onUngroupParticipants(contextMenu.groupId);
                  onClose();
                }}
              >
                Delete Group
              </button>
            </div>
          );
        }
        return null;
      })()}

      {contextMenu.type === 'participant' && (() => {
        const ctxP = participants.find((p) => p.id === contextMenu.participantId);
        const selectedParticipants = selectedItems.filter((id) => participants.some((p) => p.id === id));
        const canGroup = selectedParticipants.length >= 2;
        return (
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
              style={{ color: palette.panelText }}
              onClick={() => {
                if (ctxP) onViewParticipantInfo(ctxP.id);
                onClose();
              }}
            >
              ℹ Edit / View Info
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
              style={{ color: palette.panelText }}
              onClick={() => {
                if (ctxP) onRenameParticipant(ctxP.id);
                onClose();
              }}
            >
              ✏️ Rename
            </button>
            {canGroup && (
              <button
                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
                style={{ color: palette.panelText }}
                onClick={() => {
                  snapshotBeforeMutation();
                  onGroupParticipants(selectedParticipants);
                  onClose();
                }}
              >
                ⬜ Group {selectedParticipants.length} Participants
              </button>
            )}
            {layers.length > 1 && (
              <>
                <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
                <div
                  className="px-4 py-1 text-xs uppercase tracking-wider"
                  style={{ color: palette.panelSubText }}
                >
                  Assign to Layer
                </div>
                {layers.map((layer) => {
                  const isCurrent = (ctxP?.layerId ?? 'default') === layer.id;
                  return (
                    <button
                      key={layer.id}
                      className="w-full text-left px-6 py-1.5 text-sm transition-colors flex items-center gap-2 hover:bg-white/5"
                      style={{ color: isCurrent ? '#818cf8' : palette.panelText }}
                      onClick={() => {
                        if (ctxP) onSetParticipantLayer(ctxP.id, layer.id === 'default' ? undefined : layer.id);
                        onClose();
                      }}
                    >
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: layer.color }} />
                      {layer.name}
                      {isCurrent ? ' ✓' : ''}
                    </button>
                  );
                })}
              </>
            )}
            <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
            <button
              className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
              onClick={() => {
                if (ctxP) {
                  snapshotBeforeMutation();
                  onRemoveParticipant(ctxP.id);
                }
                onClose();
              }}
            >
              Delete Participant
            </button>
          </div>
        );
      })()}

      {contextMenu.type === 'seqMessage' && (() => {
        const ctxMsg = messages.find((m) => m.id === contextMenu.messageId);
        if (!ctxMsg) return null;
        return (
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
              style={{ color: palette.panelText }}
              onClick={() => {
                onViewMessageInfo(ctxMsg.id);
                onClose();
              }}
            >
              ℹ Edit / View Info
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
              style={{ color: palette.panelText }}
              onClick={() => {
                onEditMessageLabel(ctxMsg.id);
                onClose();
              }}
            >
              ✏️ Edit Label
            </button>
            <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
            <div
              className="px-4 py-1 text-xs uppercase tracking-wider"
              style={{ color: palette.panelSubText }}
            >
              Type
            </div>
            {(['sync', 'async', 'return'] as const).map((t) => (
              <button
                key={t}
                className="w-full text-left px-6 py-1.5 text-sm transition-colors hover:bg-white/5 flex items-center gap-2"
                style={{ color: ctxMsg.type === t ? '#818cf8' : palette.panelText }}
                onClick={() => {
                  snapshotBeforeMutation();
                  onUpdateMessageType(ctxMsg.id, t);
                  onClose();
                }}
              >
                {t === 'sync' ? '→ Sync' : t === 'async' ? '⇢ Async' : '⇠ Return'}
                {ctxMsg.type === t ? ' ✓' : ''}
              </button>
            ))}
            <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
            <div
              className="px-4 py-1 text-xs uppercase tracking-wider"
              style={{ color: palette.panelSubText }}
            >
              Color
            </div>
            <div className="flex flex-wrap gap-1.5 px-4 py-2">
              {MESSAGE_COLORS.map((c) => (
                <button
                  key={c}
                  className="w-5 h-5 rounded-full border-2 transition-transform hover:scale-110"
                  style={{
                    backgroundColor: c,
                    borderColor: ctxMsg.color === c ? '#fff' : 'transparent',
                  }}
                  onClick={() => {
                    snapshotBeforeMutation();
                    onUpdateMessageColor(ctxMsg.id, c);
                    onClose();
                  }}
                />
              ))}
              <button
                className="w-5 h-5 rounded-full border-2 flex items-center justify-center text-[9px] transition-transform hover:scale-110"
                style={{ borderColor: palette.panelBorder, color: palette.panelSubText }}
                onClick={() => {
                  snapshotBeforeMutation();
                  onUpdateMessageColor(ctxMsg.id, undefined);
                  onClose();
                }}
                title="Reset to default"
              >
                ✕
              </button>
            </div>
            <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
            <button
              className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
              style={{ color: palette.panelText }}
              onClick={() => {
                onCopyMessage(ctxMsg);
                onClose();
              }}
            >
              Copy Message
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/5"
              style={{ color: palette.panelText }}
              onClick={() => {
                onCutMessage(ctxMsg);
                onClose();
              }}
            >
              Cut Message
            </button>
            <div className="h-px mx-3 my-1" style={{ background: palette.panelBorder }} />
            <button
              className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
              onClick={() => {
                snapshotBeforeMutation();
                onRemoveMessage(ctxMsg.id);
                onClose();
              }}
            >
              Delete Message
            </button>
          </div>
        );
      })()}
    </div>
  );
}
